import { motion } from 'motion/react';
import { Shield, Users, Trophy, Check, ArrowRight, Zap, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';

const tiers = [
  {
    id: 'prospect',
    name: "PROSPECT",
    price: "FREE",
    description: "The starting point. Access the OWS booking platform and book sessions at standard rates.",
    period: "NO ONGOING COMMITMENT",
    includes: [
      "Pay-As-You-Go for All Training",
      "Standard Rate for Expeditions",
      "Official OWS Portal Access",
      "Basic Performance Tracking",
      "Eligible for Rank Advancement"
    ],
    cta: "Register as Prospect",
    link: "/syndicate",
    highlight: false
  },
  {
    id: 'member',
    name: "SYNDICATE MEMBER",
    price: "$300",
    description: "The primary entry into the elite expedition ecosystem. This seasonal initiation grants you legacy access to our highest-level operations.",
    period: "PER SEASON",
    includes: [
      "Free Members Group Trips",
      "50% Off Private Training",
      "Official Syndicate Membership & Rank",
      "Instructor-Led Safety Sessions",
      "Endurance Progression Tracking"
    ],
    cta: "Join the Syndicate",
    link: "/syndicate",
    highlight: true
  },
  {
    id: 'elite',
    name: "ELITE STATUS",
    price: "MERIT ONLY",
    description: (
      <>
        Status can only be achieved after successfully crossing Lake Ontario. No money can buy it.{' '}
        <a href="https://cert.openwatersyndicate.com" target="_blank" rel="noopener noreferrer" className="block mt-4 text-ows-teal font-bold uppercase tracking-widest text-[10px] hover:text-black transition-colors">
          Requirement to pass the test: cert.openwatersyndicate.com
        </a>
      </>
    ),
    period: "EARNED RANK",
    includes: [
      "Requires Lake Ontario Crossing",
      "Cannot Be Bought",
      "Unlocks Elite Expeditions",
      "Environment Logistics",
      "Exclusive Hardware Prototyping"
    ],
    cta: "Track Evolution Progress",
    link: "/member-portal",
    highlight: false,
    special: "earned prestige"
  }
];

export default function SyndicateMembership() {
  const { isMember } = useUser();

  return (
    <section className="py-32 bg-ows-white text-ows-dark overflow-hidden relative" id="membership-tiers">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-24">
          <span className="text-[10px] font-mono font-bold uppercase tracking-[0.4em] text-ows-teal">SYNDICATE_LEVELS</span>
          <h2 className="text-4xl md:text-7xl font-display font-light uppercase tracking-tight mt-2 italic-serif">
            MEMBERSHIP <br />
            <span className="text-ows-teal">REDEFINED.</span>
          </h2>
          <p className="text-lg text-ows-dark/60 mt-8 max-w-2xl mx-auto font-mono uppercase tracking-tighter leading-relaxed">
            Choose your rank. OWS is more than just hardware; it's access to a premium expedition culture and an elite community of explorers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`relative p-8 md:p-10 rounded-2xl border border-[#E0E0E0] bg-white transition-all duration-500 hover:shadow-xl hover:-translate-y-1`}
            >

              {tier.special && (
                <div className="flex items-center gap-2 mb-6">
                  <Star className="h-4 w-4 text-ows-orange fill-ows-orange" />
                  <span className="text-[10px] font-mono font-bold text-ows-orange uppercase tracking-widest">{tier.special}</span>
                </div>
              )}

              <div className="mb-10">
                <h3 className="text-2xl font-display font-light uppercase tracking-[0.2em] mb-2">{tier.name}</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-display font-light tracking-tight">{tier.price}</span>
                  {tier.price !== 'Custom' && tier.price !== 'EARNED RANK' && <span className="text-xs opacity-40 uppercase font-mono">/ {(tier as any).period || 'one-time'}</span>}
                </div>
                <p className={`text-[11px] font-mono uppercase tracking-tighter leading-relaxed mt-4 text-[#141414]/60`}>
                  {tier.description}
                </p>
              </div>

              <div className="space-y-4 mb-12">
                {tier.includes.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className={`h-4 w-4 mt-0.5 shrink-0 text-[#0D3A40]`} />
                    <span className={`font-sans font-light tracking-wide text-xs uppercase text-[#141414]/80 leading-relaxed`}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <Link
                to={tier.link || ''}
                className={`w-full py-5 rounded-none font-sans font-medium text-[11px] uppercase tracking-widest transition-all flex items-center justify-center gap-3 active:scale-95 ${tier.id === 'elite'
                  ? 'bg-[#0D3A40] border border-[#0D3A40] text-white hover:bg-[#0D3A40]/90'
                  : 'bg-transparent text-[#0D3A40] border border-[#0D3A40] hover:bg-[#0D3A40] hover:text-white'
                  }`}
              >
                {tier.cta}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
